import * as XLSX from 'xlsx';

/**
 * Lightweight XLSX writer for import-preview reports.
 *
 * Used by both the inventory import dialog and the wine list import dialog
 * so admins can download every preview row (errors + warnings + the
 * resolved fields shown in the inline table) when working with files large
 * enough to scroll past comfortably.
 *
 * Convention:
 *   - First column is "Row Number"
 *   - Errors / Warnings are joined with " | " so they fit one cell each
 *   - Empty rows array still writes a header-only sheet
 *   - Column widths are auto-sized to header length
 */
export interface ReportColumn<Row> {
  header: string;
  /** Returns a primitive cell value (string/number) — non-strings are stringified by XLSX. */
  value: (row: Row) => string | number | null | undefined;
}

const widthFor = (header: string): number => Math.max(header.length + 4, 14);

export function downloadImportPreviewReport<Row>(
  filename: string,
  columns: ReportColumn<Row>[],
  rows: Row[],
  sheetName = 'Preview',
): void {
  const wb = XLSX.utils.book_new();
  const headerRow = columns.map(c => c.header);
  const dataRows = rows.map(row => columns.map(c => {
    const v = c.value(row);
    return v == null ? '' : v;
  }));
  const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  ws['!cols'] = columns.map(c => ({ wch: widthFor(c.header) }));
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

/** Joins error/warning string arrays for a single XLSX cell. */
export const joinIssues = (issues: string[] | undefined): string =>
  Array.isArray(issues) && issues.length > 0 ? issues.join(' | ') : '';
